import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Home, Building2, User, Mail, Phone, Globe, Scale, AlertTriangle } from 'lucide-react';

export default function ImprintPage() {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen bg-slate-100 pt-20" data-testid="imprint-page">
      <div className="max-w-4xl mx-auto px-4 py-12">

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-military-gold/10 border border-military-gold/30 rounded-full mb-4">
            <Building2 className="w-4 h-4 text-military-gold" />
            <span className="text-military-gold text-sm">{t('legal.imprint_badge', 'Impressum (Legal Notice)')}</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-800 mb-2">
            {t('legal.imprint_title', 'IMPRINT')}
          </h1>
          <p className="text-slate-600">
            {t('legal.imprint_subtitle', 'Legal disclosure pursuant to § 5 TMG (German Telemedia Act)')}
          </p>
        </div>

        {/* Draft Notice */}
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-amber-800 font-semibold text-sm">{t('legal.draft_notice_title', 'DRAFT — REQUIRES LEGAL REVIEW')}</p>
            <p className="text-amber-700 text-sm mt-1">{t('legal.draft_notice_body', 'This document is under legal review and does not constitute final legal advice or binding terms.')}</p>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 space-y-8">

          {/* Company Information */}
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-signal-blue" />
              {t('imprint.s1.title', 'Company Information (Angaben gemäß § 5 TMG)')}
            </h2>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-slate-700 space-y-3">
              <p className="text-lg font-semibold text-slate-800">{t('imprint.s1.name', 'TLA Housing Europe')}</p>
              <p>{t('imprint.s1.legal_name', '[Legal Company Name / Firmenname]')}</p>
              <p>{t('imprint.s1.street', '[Street Address / Straße und Hausnummer]')}</p>
              <p>{t('imprint.s1.city', '[Postal Code] [City], Germany')}</p>
            </div>
            <p className="text-amber-700 text-sm mt-3 italic">
              {t('imprint.s1.notice', '* Company registration details must be completed before commercial operations commence.')}
            </p>
          </section>

          {/* Management */}
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-signal-blue" />
              {t('imprint.s2.title', 'Represented By (Vertreten durch)')}
            </h2>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-slate-700">
              <p><strong>{t('imprint.s2.director_label', 'Managing Director / Geschäftsführer:')}</strong> {t('imprint.s2.director_placeholder', '[Name]')}</p>
            </div>
          </section>

          {/* Contact */}
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-signal-blue" />
              {t('imprint.s3.title', 'Contact (Kontakt)')}
            </h2>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-slate-700 space-y-2">
              <p className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-slate-800" />
                <strong>{t('imprint.s3.phone_label', 'Phone:')}</strong> {t('imprint.s3.phone_value', '+49 [Number]')}
              </p>
              <p className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-800" />
                <strong>{t('imprint.s3.email_label', 'Email:')}</strong>{' '}
                <a href="mailto:info@tlahousing.eu" className="text-signal-blue hover:underline">
                  info@tlahousing.eu
                </a>
              </p>
              <p className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-slate-800" />
                <strong>{t('imprint.s3.website_label', 'Website:')}</strong>{' '}
                <a href="https://tlahousing.eu" className="text-signal-blue hover:underline">
                  www.tlahousing.eu
                </a>
              </p>
            </div>
          </section>

          {/* Registration */}
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">
              {t('imprint.s4.title', 'Commercial Register (Handelsregister)')}
            </h2>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-slate-700 space-y-2">
              <p><strong>{t('imprint.s4.court_label', 'Registration Court / Registergericht:')}</strong> {t('imprint.s4.court_value', '[Amtsgericht Stadt]')}</p>
              <p><strong>{t('imprint.s4.number_label', 'Registration Number / Registernummer:')}</strong> {t('imprint.s4.number_value', 'HRB [Number]')}</p>
            </div>
          </section>

          {/* VAT ID */}
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">
              {t('imprint.s5.title', 'VAT Identification Number (Umsatzsteuer-ID)')}
            </h2>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-slate-700">
              <p><strong>{t('imprint.s5.label', 'VAT ID pursuant to § 27a UStG:')}</strong></p>
              <p>{t('imprint.s5.value', 'DE [VAT Number]')}</p>
            </div>
          </section>

          {/* Responsible for Content */}
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">
              {t('imprint.s6.title', 'Responsible for Content (Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV)')}
            </h2>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-slate-700">
              <p>{t('imprint.s6.name_placeholder', '[Name of responsible person]')}</p>
              <p>{t('imprint.s6.address_placeholder', '[Address]')}</p>
            </div>
          </section>

          {/* EU Dispute Resolution */}
          <section className="bg-slate-50 border border-slate-200 rounded-lg p-6">
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Scale className="w-5 h-5 text-signal-blue" />
              {t('imprint.s7.title', 'EU Online Dispute Resolution (OS-Plattform)')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('imprint.s7.p', 'The European Commission provides a platform for online dispute resolution (ODR) for consumers:')}
            </p>
            <a
              href="https://ec.europa.eu/consumers/odr"
              target="_blank"
              rel="noopener noreferrer"
              className="text-signal-blue hover:underline text-lg"
            >
              https://ec.europa.eu/consumers/odr
            </a>
            <p className="text-slate-600 leading-relaxed mt-4">
              {t('imprint.s7.p2', 'Our email address can be found above. We are not obligated and not willing to participate in dispute resolution proceedings before a consumer arbitration board.')}
            </p>
          </section>

          {/* Liability for Content */}
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">
              {t('imprint.s8.title', 'Liability for Content (Haftung für Inhalte)')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('imprint.s8.p1', 'As a service provider, we are responsible for our own content on these pages in accordance with § 7 para. 1 TMG (German Telemedia Act). However, according to §§ 8 to 10 TMG, as a service provider, we are not obligated to monitor transmitted or stored third-party information or to investigate circumstances that indicate illegal activity.')}
            </p>
            <p className="text-slate-600 leading-relaxed">
              {t('imprint.s8.p2', 'Obligations to remove or block the use of information under general law remain unaffected. However, liability in this regard is only possible from the point in time at which we become aware of a specific legal violation. Upon becoming aware of such legal violations, we will remove this content immediately.')}
            </p>
          </section>

          {/* Liability for Links */}
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">
              {t('imprint.s9.title', 'Liability for Links (Haftung für Links)')}
            </h2>
            <p className="text-slate-600 leading-relaxed">
              {t('imprint.s9.p', 'Our website contains links to external websites of third parties over whose content we have no control. Therefore, we cannot accept any liability for this third-party content. The respective provider or operator of the pages is always responsible for the content of the linked pages. The linked pages were checked for possible legal violations at the time of linking. Illegal content was not recognizable at the time of linking. However, permanent monitoring of the content of the linked pages is not reasonable without concrete evidence of a legal violation. If we become aware of any legal violations, we will remove such links immediately.')}
            </p>
          </section>

          {/* Copyright */}
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">
              {t('imprint.s10.title', 'Copyright (Urheberrecht)')}
            </h2>
            <p className="text-slate-600 leading-relaxed">
              {t('imprint.s10.p', 'The content and works created by the site operators on these pages are subject to German copyright law. Duplication, processing, distribution, and any kind of exploitation outside the limits of copyright law require the written consent of the respective author or creator. Downloads and copies of this site are only permitted for private, non-commercial use. Insofar as the content on this site was not created by the operator, the copyrights of third parties are respected. In particular, third-party content is marked as such. Should you nevertheless become aware of a copyright infringement, please inform us accordingly. If we become aware of any infringements, we will remove such content immediately.')}
            </p>
          </section>

          {/* Professional Regulations */}
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">
              {t('imprint.s11.title', 'Professional Regulations (Berufsrechtliche Regelungen)')}
            </h2>
            <p className="text-slate-600 leading-relaxed">
              {t('imprint.s11.p', 'TLA Housing Europe operates as an online marketplace platform connecting property owners with potential guests. We are not a licensed real estate agent (Immobilienmakler) and do not engage in brokerage activities that would require registration under § 34c GewO. Property listings are published by property owners directly.')}
            </p>
          </section>

        </div>

        {/* Related Links */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mt-8">
          <h3 className="font-heading text-lg font-bold text-slate-800 mb-4">{t('legal.related_docs', 'Related Legal Documents')}</h3>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/terms"
              className="text-signal-blue hover:underline flex items-center gap-2"
            >
              <Scale className="w-4 h-4" />
              {t('imprint.link_terms', 'Terms of Service (AGB)')}
            </Link>
            <Link
              to="/privacy"
              className="text-signal-blue hover:underline flex items-center gap-2"
            >
              <Scale className="w-4 h-4" />
              {t('imprint.link_privacy', 'Privacy Policy (Datenschutz)')}
            </Link>
          </div>
        </div>

        {/* Back Link */}
        <div className="text-center mt-8">
          <Link to="/" className="text-signal-blue hover:text-signal-blue-hover inline-flex items-center gap-2">
            <Home className="w-4 h-4" />
            {t('legal.back_home', 'Back to Home')}
          </Link>
        </div>

      </div>
    </div>
  );
}
